import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/store/auth'
import type { Message } from '@/api/conclave'
import { Button } from '@diwata/ironvale'

export function Chat() {
  const { id } = useParams<{ id: string }>()
  const api = useAuth((s) => s.api)()

  const [messages, setMessages] = useState<Message[]>([])
  const [draft, setDraft] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [streamingText, setStreamingText] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useQuery({
    queryKey: ['history', id],
    queryFn: async () => {
      const data = await api.getHistory(id!)
      setMessages(data.messages)
      return data
    },
    enabled: !!id,
  })

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingText])

  async function send() {
    if (!draft.trim() || streaming) return
    const text = draft.trim()
    setDraft('')
    setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'user', content: text, created_at: new Date().toISOString() }])
    setStreaming(true)
    setStreamingText('')

    try {
      const stream = await api.chatStream(id!, text)
      const reader = stream.getReader()
      const decoder = new TextDecoder()
      let accumulated = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        const chunk = decoder.decode(value, { stream: true })
        for (const line of chunk.split('\n')) {
          if (!line.startsWith('data: ')) continue
          const payload = line.slice(6).trim()
          if (payload === '[DONE]') break
          try {
            const { text: t } = JSON.parse(payload)
            accumulated += t
            setStreamingText(accumulated)
          } catch {}
        }
      }

      setMessages((prev) => [...prev, {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: accumulated,
        created_at: new Date().toISOString(),
      }])
    } finally {
      setStreaming(false)
      setStreamingText('')
      inputRef.current?.focus()
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() }
  }

  return (
    <div className="cp-chat">
      <div className="cp-chat-messages">
        {messages.map((m) => (
          <div key={m.id} className={`cp-chat-message cp-chat-message--${m.role}`}>
            <div className="cp-chat-bubble">{m.content}</div>
          </div>
        ))}
        {streaming && streamingText && (
          <div className="cp-chat-message cp-chat-message--assistant">
            <div className="cp-chat-bubble">{streamingText}</div>
          </div>
        )}
        {streaming && !streamingText && (
          <div className="cp-chat-message cp-chat-message--assistant">
            <div className="cp-chat-bubble" style={{ color: 'var(--ae-color-neutral-400)' }}>…</div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="cp-chat-input-row">
        <textarea
          ref={inputRef}
          className="cp-textarea cp-chat-input"
          placeholder="Message…"
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={streaming}
          style={{ resize: 'none' }}
        />
        <Button onClick={send} disabled={streaming || !draft.trim()}>Send</Button>
      </div>
    </div>
  )
}
